"use client";

import Breadcrumbs from "@/components/layout/Breadcrumbs";
import Link from "next/link";
import { Mail, Clock, ShieldCheck, MessageSquare, FileQuestion, Lightbulb, ArrowLeft } from "lucide-react";

export default function ContactPage() {
  const contactPageSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Get in Touch — FinanceLives",
    "description": "Contact FinanceLives for bug reports, questions, feature suggestions, and support. We respond within 24-48 hours.",
    "url": "https://financelives.com/contact",
    "inLanguage": "en",
    "mainEntity": {
      "@type": "Organization",
      "name": "FinanceLives",
      "email": "contact@financelives.com",
      "contactType": "customer support",
      "url": "https://financelives.com"
    }
  };

  return (
    <div className="contact-page-wrapper">
      <div className="container py-8 anim-fade-in">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(contactPageSchema) }}
        />
        <Breadcrumbs />

        {/* Back link */}
        <div className="back-link-wrapper">
          <Link href="/" className="back-link">
            <ArrowLeft size={16} /> Back to Home
          </Link>
        </div>

        {/* Hero Section */}
        <div className="contact-hero">
          <div className="hero-content">
            <h1 className="hero-title">Get in Touch</h1>
            <p className="hero-description">
              We're here to help with your questions, feedback, or suggestions. 
              Whether you've found a bug, need calculator support, or have an idea 
              for a new tool—we'd love to hear from you.
            </p>
          </div>
        </div>

        {/* Main Contact Card */}
        <div className="contact-main-card">
          <div className="card-header">
            <div className="icon-wrapper">
              <Mail size={32} />
            </div>
            <h2>Email Support</h2>
          </div>
          
          <p className="card-description">
            Reach out to us directly for any inquiries, bug reports, feature requests, 
            or general questions about our financial calculators and tools.
          </p>
          
          <a href="mailto:contact@financelives.com" className="email-button">
            <Mail size={20} />
            <span>contact@financelives.com</span>
          </a>

          <div className="info-items">
            <div className="info-item">
              <Clock size={20} />
              <div>
                <h3>Response Time</h3>
                <p>We typically respond within 24-48 business hours</p>
              </div>
            </div>

            <div className="info-item">
              <ShieldCheck size={20} />
              <div>
                <h3>Privacy First</h3>
                <p>Your communications are secure and we never collect unnecessary data</p>
              </div>
            </div>
          </div>
        </div>

        {/* What You Can Contact Us About */}
        <div className="contact-topics">
          <h2 className="topics-title">What Can We Help You With?</h2>
          <div className="topics-grid">
            <div className="topic-item">
              <div className="topic-icon">
                <FileQuestion size={22} />
              </div>
              <div>
                <h3>Bug Reports</h3>
                <p>Found an issue with a calculator? Let us know so we can fix it. Include the calculator you were using, the inputs entered, and what result you expected versus what you saw.</p>
              </div>
            </div>
            <div className="topic-item">
              <div className="topic-icon">
                <MessageSquare size={22} />
              </div>
              <div>
                <h3>Questions & Support</h3>
                <p>Need help understanding how a tool works? We're here to assist. Whether you are unsure about tax bracket calculations, retirement projections, or loan amortization schedules, our team can explain the methodology and assumptions behind each calculator.</p>
              </div>
            </div>
            <div className="topic-item">
              <div className="topic-icon">
                <Lightbulb size={22} />
              </div>
              <div>
                <h3>Feature Suggestions</h3>
                <p>Have an idea for a new calculator or comparison tool? Share it with us! We regularly add new tools based on user requests and update existing calculators when tax laws, contribution limits, or interest rates change each financial year.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="contact-additional-info">
          <h2 className="topics-title">Before You Reach Out</h2>
          <p className="info-note">
            Most financial calculators on FinanceLives process all data locally in your browser. No information you enter is sent to our servers or stored anywhere. If you are experiencing unexpected results, try refreshing the page or clearing your browser cache, as outdated cached scripts can sometimes cause display issues. For questions about specific tax rules or regulatory limits, we recommend cross-referencing with your local tax authority's official publications, as laws and brackets can change between updates.
          </p>
          <p className="info-note" style={{ marginTop: 'var(--space-3)' }}>
            We strive to respond to all inquiries within 24-48 business hours. For the fastest assistance, please include your country, the specific calculator or tool you are using, and a brief description of the issue or question. This helps our team identify the relevant tax rules and provide accurate guidance tailored to your situation.
          </p>
        </div>
      </div>

      <style jsx>{`
        .contact-page-wrapper {
          min-height: 60vh;
          padding-bottom: var(--space-12);
        }

        /* Hero Section */
        .contact-hero {
          text-align: center;
          padding: var(--space-8) 0 var(--space-10);
          margin-bottom: var(--space-6);
          background: radial-gradient(circle at 50% 0%, var(--primary-light) 0%, transparent 60%);
        }

        .hero-content {
          max-width: 700px;
          margin: 0 auto;
        }

        .hero-title {
          font-size: var(--text-4xl);
          font-weight: var(--fw-extrabold);
          color: var(--text-primary);
          margin-bottom: var(--space-4);
          letter-spacing: -0.02em;
        }

        .hero-description {
          font-size: var(--text-md);
          line-height: 1.7;
          color: var(--text-secondary);
          margin: 0;
        }

        /* Main Contact Card */
        .contact-main-card {
          background: var(--bg-surface);
          padding: var(--space-8);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-xl);
          border: 1px solid var(--border-color);
          margin-bottom: var(--space-10);
          position: relative;
          overflow: hidden;
        }

        .contact-main-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
          opacity: 0.05;
          z-index: 0;
        }

        .contact-main-card > * {
          position: relative;
          z-index: 1;
        }

        .card-header {
          display: flex;
          align-items: center;
          gap: var(--space-4);
          margin-bottom: var(--space-5);
        }

        .icon-wrapper {
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: var(--radius-md);
          background: var(--primary-light);
          color: var(--primary);
        }

        .card-header h2 {
          font-size: var(--text-2xl);
          font-weight: var(--fw-bold);
          margin: 0;
          color: var(--text-primary);
        }

        .card-description {
          font-size: var(--text-md);
          line-height: 1.7;
          margin-bottom: var(--space-6);
          color: var(--text-secondary);
        }

        .email-button {
          display: inline-flex;
          align-items: center;
          gap: var(--space-3);
          padding: var(--space-4) var(--space-6);
          background: var(--primary);
          color: var(--text-on-accent);
          text-decoration: none;
          border-radius: var(--radius-md);
          font-size: var(--text-md);
          font-weight: var(--fw-bold);
          transition: all var(--transition-normal);
          box-shadow: var(--shadow-md);
          margin-bottom: var(--space-8);
        }

        .email-button:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-lg);
          background: var(--primary-hover);
        }

        /* Info Items */
        .info-items {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: var(--space-6);
          padding-top: var(--space-6);
          border-top: 1px solid var(--border-color);
        }

        .info-item {
          display: flex;
          gap: var(--space-3);
          align-items: flex-start;
          color: var(--text-primary);
        }

        .info-item svg {
          flex-shrink: 0;
          margin-top: 2px;
          color: var(--primary);
        }

        .info-item h3 {
          font-size: var(--text-lg);
          font-weight: var(--fw-bold);
          margin: 0 0 var(--space-2) 0;
          color: var(--text-primary);
        }

        .info-item p {
          font-size: var(--text-sm);
          line-height: 1.6;
          margin: 0;
          color: var(--text-secondary);
        }

        /* Contact Topics Section */
        .contact-topics {
          background: var(--bg-surface);
          padding: var(--space-8);
          border-radius: var(--radius-lg);
          border: 1px solid var(--border-color);
          margin-bottom: var(--space-8);
          box-shadow: var(--shadow-sm);
        }

        .topics-title {
          font-size: var(--text-2xl);
          font-weight: var(--fw-bold);
          color: var(--text-primary);
          text-align: center;
          margin-bottom: var(--space-6);
        }

        .topics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: var(--space-6);
        }

        .topic-item {
          display: flex;
          gap: var(--space-4);
          padding: var(--space-5);
          background: var(--bg-main);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          transition: all var(--transition-normal);
        }

        .topic-item:hover {
          border-color: var(--primary);
          background: var(--bg-surface-hover);
        }

        .topic-icon {
          flex-shrink: 0;
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--primary-light);
          color: var(--primary);
          border-radius: var(--radius-md);
        }

        .topic-item h3 {
          font-size: var(--text-lg);
          font-weight: var(--fw-bold);
          color: var(--text-primary);
          margin-bottom: var(--space-2);
        }

        .topic-item p {
          font-size: var(--text-sm);
          line-height: 1.5;
          color: var(--text-secondary);
          margin: 0;
        }

        /* Additional Info Section */
        .contact-additional-info {
          background: var(--bg-surface);
          padding: var(--space-8);
          border-radius: var(--radius-lg);
          border: 1px solid var(--border-color);
          margin-bottom: var(--space-8);
          box-shadow: var(--shadow-sm);
        }

        .info-note {
          font-size: var(--text-sm);
          line-height: 1.7;
          color: var(--text-secondary);
          margin: 0;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .contact-hero {
            padding: var(--space-6) 0 var(--space-8);
          }

          .hero-title {
            font-size: var(--text-3xl);
          }

          .hero-description {
            font-size: var(--text-base);
          }

          .contact-main-card {
            padding: var(--space-6);
          }

          .card-header {
            flex-direction: column;
            text-align: center;
          }

          .card-header h2 {
            font-size: var(--text-2xl);
          }

          .card-description {
            text-align: center;
          }

          .email-button {
            width: 100%;
            justify-content: center;
            font-size: var(--text-sm);
          }

          .info-items {
            grid-template-columns: 1fr;
          }

          .info-item {
            flex-direction: column;
            align-items: center;
            text-align: center;
          }

          .contact-topics {
            padding: var(--space-6);
          }

          .topics-grid {
            grid-template-columns: 1fr;
          }

          .topic-item {
            flex-direction: column;
            text-align: center;
          }

          .topic-icon {
            margin: 0 auto;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .info-card:hover,
          .topic-item:hover {
            transform: none;
          }
          
          .email-button:hover {
            transform: none;
          }
        }
      `}</style>
    </div>
  );
}
